import { Link } from "react-router-dom";

function Section({ n, title, children }) {
  return (
    <div className="reveal">
      <h2 className="font-display font-bold text-xl md:text-2xl text-white mt-10 mb-3">
        <span className="font-mono text-[#F97316] mr-2">{n}.</span>
        {title}
      </h2>
      <div className="text-[#94A3B8] leading-relaxed space-y-3 text-[15px]">{children}</div>
    </div>
  );
}

export default function Privacy() {
  return (
    <section className="section-y relative">
      <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
      <div className="container-x relative max-w-3xl">
        <div className="reveal">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#F97316] mb-3">Legal</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-white tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm text-[#94A3B8] font-mono">Last updated: July 2, 2026</p>

          <p className="mt-6 text-[#94A3B8] leading-relaxed text-[15px]">
            LocateOS Inc. ("LocateOS," "we," "us," or "our") operates the LocateOS mobile application (the "App") and the website at{" "}
            <a href="https://locateos.com" className="text-[#F97316] hover:underline">locateos.com</a>. This Privacy Policy explains what information we collect, how we use it, and the choices you have. By using the App, you agree to the practices described here.
          </p>
        </div>

        <Section n="1" title="Who Uses LocateOS">
          <p>
            LocateOS is a professional ticket-management application for utility-locating operations. It is used by field technicians ("locators"), supervisors, and administrators employed by or contracted to organizations that subscribe to LocateOS. The App is intended for business use and is not directed to children.
          </p>
        </Section>

        <Section n="2" title="Information We Collect">
          <p>We collect only the information needed to operate the App and support utility-locating work:</p>
          <p>
            <span className="text-white font-semibold">Account information.</span> When you sign in, we process your username, name, role, and organization. Passwords are handled securely and are not stored in readable form.
          </p>
          <p>
            <span className="text-white font-semibold">Location data.</span> With your permission, the App collects your device's GPS location to record where locate work is performed and to embed location coordinates into field photos for verification and compliance. Location is collected only while you are using the App.
          </p>
          <p>
            <span className="text-white font-semibold">Photos.</span> When you capture a site photo through the App, the photo — together with the date, time, and GPS coordinates at the moment of capture — is stored so it can be attached to the relevant ticket.
          </p>
          <p>
            <span className="text-white font-semibold">Work and usage data.</span> We process the ticket, response, note, time-entry, and work-item data you enter, along with basic technical information (such as app version and device type) needed to operate and troubleshoot the service.
          </p>
          <p className="text-white">
            We do <span className="font-bold">NOT</span> collect data for advertising, and we do <span className="font-bold">NOT</span> track you across other apps or websites.
          </p>
        </Section>

        <Section n="3" title="How We Use Information">
          <p>We use the information above to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Authenticate you and provide access to the App</li>
            <li>Display, assign, and manage locate tickets</li>
            <li>Record locate responses, notes, photos, and time entries</li>
            <li>Embed GPS and timestamp information into field photos for compliance and verification</li>
            <li>Sync your work to your organization's records in real time</li>
            <li>Maintain security, prevent misuse, and troubleshoot issues</li>
          </ul>
        </Section>

        <Section n="4" title="How Information Is Shared">
          <p>
            Your work data is shared with your own organization — the supervisors and administrators of the company you work for or with — because LocateOS is an operational tool for that organization. We do not sell your personal information. We may share information with service providers who host and maintain the App on our behalf (for example, cloud hosting and storage providers), and when required by law.
          </p>
        </Section>

        <Section n="5" title="Data Storage and Security">
          <p>
            Data is stored on secured cloud infrastructure (Microsoft Azure). We use industry-standard measures, including encryption in transit (HTTPS/TLS), to protect your information. No method of transmission or storage is completely secure, but we work to protect your data using reasonable safeguards.
          </p>
        </Section>

        <Section n="6" title="Data Retention">
          <p>
            We retain ticket, response, photo, and time data for as long as your organization requires it for operational and regulatory recordkeeping, and as configured by your organization's administrators. Account information is retained while your account is active.
          </p>
        </Section>

        <Section n="7" title="Your Choices and Rights">
          <p>You can:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Control location access at any time in your device settings</li>
            <li>Request access to, correction of, or deletion of your personal information by contacting us</li>
            <li>
              Delete your account from within the App (<span className="text-white">Profile → Delete Account</span>), which deactivates your account. Certain records may be retained where required for your organization's legal or regulatory recordkeeping.
            </li>
          </ul>
          <p>Depending on where you live, you may have additional rights under laws such as the CCPA or GDPR. Contact us to exercise any applicable rights.</p>
        </Section>

        <Section n="8" title="Children's Privacy">
          <p>
            LocateOS is a workplace tool and is not intended for anyone under 18. We do not knowingly collect information from children.
          </p>
        </Section>

        <Section n="9" title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date.
          </p>
        </Section>

        <Section n="10" title="Contact Us">
          <p>If you have questions about this Privacy Policy or your data, contact us at:</p>
          <div className="mt-4 rounded-2xl glass p-5">
            <div className="text-white font-semibold">LocateOS Inc.</div>
            <div>Email: <a className="text-[#F97316] hover:underline" href="mailto:info@locateos.com">info@locateos.com</a></div>
            <div>Website: <a className="text-[#F97316] hover:underline" href="https://locateos.com">https://locateos.com</a></div>
          </div>
        </Section>

        <div className="reveal mt-14 pt-8 border-t border-white/5 flex items-center justify-between text-sm">
          <Link to="/" className="text-[#94A3B8] hover:text-white">← Back to home</Link>
          <Link to="/support" className="text-[#F97316] hover:underline">Support →</Link>
        </div>
      </div>
    </section>
  );
}
